import { useRef, useState } from "react";
import { Film, X, Paperclip } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const MAX = 500 * 1024 * 1024;
const OK = /\.(mp4|m4v|mov)$/i;

export function VideoUpload({ fileName, onUploaded, testId }) {
  const [pct, setPct] = useState(null);
  const [error, setError] = useState(null);
  const ctl = useRef(null);
  const uploadId = useRef(null);

  const start = async (file) => {
    setError(null);
    if (!OK.test(file.name)) return setError("Only MP4, M4V or MOV files can be uploaded.");
    if (file.size > MAX) return setError(`This file is ${Math.round(file.size / 1048576)} MB. The limit is 500 MB.`);
    ctl.current = new AbortController();
    setPct(0);
    try {
      const { data } = await api.post("/uploads/video", { filename: file.name, size: file.size });
      uploadId.current = data.upload_id;
      for (let n = 0; n < data.parts; n++) {
        const blob = file.slice(n * data.chunk_size, Math.min(file.size, (n + 1) * data.chunk_size));
        let tries = 0;
        for (;;) {
          try {
            await api.put(`/uploads/video/${data.upload_id}/parts/${n}`, blob, {
              headers: { "Content-Type": "application/octet-stream" }, signal: ctl.current.signal,
              onUploadProgress: (ev) => setPct(Math.min(99, Math.round(((n * data.chunk_size + ev.loaded) / file.size) * 100))),
            });
            break;
          } catch (e) {
            if (ctl.current.signal.aborted || ++tries >= 3) throw e;
          }
        }
      }
      const { data: rec } = await api.post(`/uploads/video/${data.upload_id}/complete`);
      onUploaded(rec);
    } catch (e) {
      if (ctl.current?.signal.aborted) setError("Upload cancelled.");
      else setError(formatApiError(e));
    } finally {
      setPct(null);
      uploadId.current = null;
    }
  };
  const cancel = () => {
    ctl.current?.abort();
    if (uploadId.current) api.delete(`/uploads/video/${uploadId.current}`).catch(() => {});
  };

  return (
    <div className="space-y-2">
      {pct === null ? (
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-lasso-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-lasso-navy" data-testid={`${testId}-button`}>
          <Film className="h-4 w-4" /> Upload MP4 <span className="text-xs font-normal text-white/70">up to 500 MB</span>
          <input type="file" accept=".mp4,.m4v,.mov,video/mp4,video/quicktime" className="hidden" data-testid={testId} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) start(f); }} />
        </label>
      ) : (
        <div className="flex items-center gap-3" data-testid={`${testId}-progress`}>
          <div className="h-2 w-64 overflow-hidden rounded-full bg-lasso-tint"><div className="h-full bg-lasso-ink transition-[width]" style={{ width: `${pct}%` }} /></div>
          <span className="text-xs font-semibold" data-testid={`${testId}-pct`}>{pct}%</span>
          <button type="button" onClick={cancel} className="inline-flex items-center gap-1 text-xs font-semibold text-rose-600" data-testid={`${testId}-cancel`}><X className="h-3.5 w-3.5" /> Cancel</button>
        </div>
      )}
      {fileName && pct === null && <p className="flex items-center gap-1.5 text-xs text-zinc-600" data-testid={`${testId}-name`}><Paperclip className="h-3 w-3" />{fileName}</p>}
      {error && <p className="text-xs font-semibold text-rose-600" data-testid={`${testId}-error`}>{error}</p>}
    </div>
  );
}
